import { useEffect } from "react";
import { authFetch } from "../lib/authFetch";
import { X, Tag, Sparkles, Info, Calendar, Package, Download } from "lucide-react";

export type ActualiteUnified = {
  id: string;
  titre: string;
  accroche: string | null;
  contenu: string | null;
  imageUrl: string | null;
  documentId: string | null;
  type: string | null;
  createdAt: string;
  prixPromo: string | null;
  prixNormal: string | null;
  produitId: string | null;
  produitNom: string | null;
  produitSku: string | null;
  produitImageUrl: string | null;
  dateFin: string | null;
  dateDebut: string | null;
  isBundle: boolean | null;
  documentPdfUrl: string | null;
};

function formatDate(d?: string | null) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

function fmtCurrency(v: string | null | number) {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  return isNaN(n) ? "—" : n.toLocaleString("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 });
}

export function ActuDetailPanel({ actu, onClose }: { actu: ActualiteUnified; onClose: () => void }) {
  const isPromo = actu.type === "promotion";
  const isInfo  = actu.type === "information";

  const displayTitle = isPromo ? (actu.produitNom || actu.titre) : actu.titre;

  const prixNormalNum = actu.prixNormal ? parseFloat(actu.prixNormal) : null;
  const prixPromoNum  = actu.prixPromo  ? parseFloat(actu.prixPromo)  : null;
  const economie = prixNormalNum !== null && prixPromoNum !== null ? prixNormalNum - prixPromoNum : null;
  const pourcentageRemise =
    prixNormalNum && prixPromoNum && prixNormalNum > 0
      ? Math.round(((prixNormalNum - prixPromoNum) / prixNormalNum) * 100)
      : null;

  const badge = isPromo
    ? { label: "Promotion",   Icon: Tag,      cls: "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400", headerCls: "text-orange-500" }
    : isInfo
    ? { label: "Information", Icon: Info,     cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",           headerCls: "text-gray-400"   }
    : { label: "Nouveauté",   Icon: Sparkles, cls: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400",         headerCls: "text-primary"    };

  // Fermeture sur Échap
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Traçabilité lecture (fire-and-forget)
  useEffect(() => {
    authFetch(`/api/portail/actualites/${actu.id}/lecture`, { method: "POST" }).catch(() => {});
  }, [actu.id]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="relative h-full w-full max-w-lg bg-background border-l shadow-2xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="actu-detail-panel"
      >
        {/* ── En-tête ── */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <badge.Icon className={`w-4 h-4 shrink-0 ${badge.headerCls}`} />
            <span className="font-semibold text-sm truncate">{displayTitle}</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted shrink-0"
            data-testid="btn-close-actu-panel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Image de couverture ── */}
        {actu.imageUrl && (
          <img
            src={actu.imageUrl}
            alt={displayTitle}
            className="w-full h-52 object-cover shrink-0"
          />
        )}

        {/* ── Corps scrollable ── */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Badge type */}
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${badge.cls}`}>
            <badge.Icon className="w-3 h-3" />
            {badge.label}
          </span>

          {/* Titre */}
          <h2 className="text-lg font-bold leading-snug">{displayTitle}</h2>

          {/* Accroche */}
          {actu.accroche && (
            <p className="text-sm font-medium text-foreground/80 leading-relaxed">
              {actu.accroche}
            </p>
          )}

          {/* ── Blocs spécifiques Promotion ── */}
          {isPromo && (
            <>
              {/* Bloc prix mis en valeur */}
              {(prixNormalNum !== null || prixPromoNum !== null) && (
                <div className="bg-orange-50 dark:bg-orange-950/20 rounded-xl p-4 border border-orange-100 dark:border-orange-900/30">
                  <p className="text-[11px] font-semibold text-orange-700 dark:text-orange-400 uppercase tracking-wide mb-2">
                    Prix promotionnel
                  </p>
                  <div className="flex items-end gap-3 flex-wrap">
                    {prixNormalNum !== null && (
                      <span className="text-lg text-muted-foreground line-through">
                        {fmtCurrency(prixNormalNum)}
                      </span>
                    )}
                    {prixPromoNum !== null && (
                      <span className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                        {fmtCurrency(prixPromoNum)}
                      </span>
                    )}
                  </div>
                  {pourcentageRemise !== null && pourcentageRemise > 0 && (
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-sm bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 px-2 py-0.5 rounded-full font-semibold">
                        -{pourcentageRemise}%
                      </span>
                      {economie !== null && economie > 0 && (
                        <span className="text-xs text-orange-600 dark:text-orange-400">
                          Économisez {fmtCurrency(economie)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Produit lié */}
              {actu.produitId && (actu.produitNom || actu.produitImageUrl) && (
                <div className="border border-border rounded-xl p-3 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-muted/40 border border-border overflow-hidden shrink-0 flex items-center justify-center">
                    {actu.produitImageUrl
                      ? <img src={actu.produitImageUrl} alt={actu.produitNom || ""} className="w-full h-full object-contain p-1" />
                      : <Package className="w-6 h-6 text-muted-foreground/40" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">Produit concerné</p>
                    <p className="font-semibold text-sm truncate mt-0.5">{actu.produitNom || "—"}</p>
                    {actu.produitSku && (
                      <p className="text-xs font-mono text-muted-foreground">{actu.produitSku}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Période de validité */}
              {(actu.dateDebut || actu.dateFin) && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 bg-muted/30 rounded-lg px-3 py-2">
                  <Calendar className="w-3.5 h-3.5 shrink-0" />
                  {actu.dateDebut && actu.dateFin
                    ? `Du ${formatDate(actu.dateDebut)} au ${formatDate(actu.dateFin)}`
                    : actu.dateFin
                    ? `Valable jusqu'au ${formatDate(actu.dateFin)}`
                    : `À partir du ${formatDate(actu.dateDebut)}`}
                </p>
              )}
            </>
          )}

          {/* Contenu long (tous types) */}
          {actu.contenu && (
            <div className="text-sm text-foreground/70 whitespace-pre-wrap leading-relaxed border-t border-border/50 pt-3">
              {actu.contenu}
            </div>
          )}

          {/* Lien document PDF (tous types) */}
          {actu.documentPdfUrl && (
            <a
              href={actu.documentPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-primary hover:underline border border-border rounded-lg px-3 py-2 w-full transition-colors hover:bg-muted/30"
              data-testid="btn-download-pdf-panel"
            >
              <Download className="w-4 h-4 shrink-0" />
              Télécharger le document associé (PDF)
            </a>
          )}

          {/* Date de publication */}
          <div className="text-xs text-muted-foreground pt-2 border-t border-border/50">
            Publié le {formatDate(actu.createdAt)}
          </div>
        </div>
      </div>
    </div>
  );
}
